/* B2B Chairs landing page — custom element + scroll helper.
 *
 * Loaded once globally (via the hero section). Guards against re-init if a
 * second include ever lands on the page.
 */
(function () {
  if (window.__b2bChairsInit) return;
  window.__b2bChairsInit = true;

  /* ----- Chair spec data ------------------------------------------------
   * Per-chair data comes from the
   *   Tectonic.b2bChairsPageComparisonTableData
   * product metafield, pre-rendered into `window.__b2bCompareData` by the
   * compare section's Liquid template (one entry per product handle).
   *
   * Metafield shape per product:
   *   { data: [
   *       { "Index List Title": "Main",
   *         "List": [{ key: "<category>", value: "<description>" }] },
   *       { "Index List Title": "Ergonomic Design",
   *         "List": [
   *           { key: "Recline (°)", value: "Up to 135°", icon: "https://cdn.../Recline.png" },
   *           ...
   *         ] },
   *       ...
   *     ] }
   *
   * Per-row "icon" is optional — if present (full URL), it renders as the
   * left-column icon image. If absent, a placeholder circle is shown.
   * The "Main" section is special — its first row carries the chair's
   * marketing category (key) and short description (value). Everything else
   * becomes a spec section in the accordion. */
  function parseChairMetafield(raw) {
    const blank = { category: '', description: '', specs: [] };
    if (!raw) return blank;

    let data = raw;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch (_) { return blank; }
    }
    // Tolerate either { data: [...] } or { field: { data: [...] } } shapes.
    const sections = data?.data || data?.field?.data || [];
    if (!Array.isArray(sections)) return blank;

    let category = '';
    let description = '';
    const specs = [];

    for (const s of sections) {
      const title = String(s?.['Index List Title'] || s?.title || '').trim();
      const list  = Array.isArray(s?.List) ? s.List : (Array.isArray(s?.list) ? s.list : []);
      if (!title) continue;

      if (/^main$/i.test(title)) {
        const first = list[0];
        if (first) {
          category    = String(first.key   || '').trim();
          description = String(first.value || '').trim();
        }
        continue;
      }

      const rows = list
        .map((r) => ({
          key:   String(r?.key   || '').trim(),
          value: String(r?.value || '').trim(),
          icon:  String(r?.icon  || '').trim(),
        }))
        .filter((r) => r.key);

      if (rows.length) specs.push({ title, rows });
    }

    return { category, description, specs };
  }

  function getChairSpecs(handle) {
    const raw = window.__b2bCompareData?.[handle];
    if (!raw) {
      console.warn(
        '[b2b-compare] No metafield data for handle "' + handle + '".',
        'Check that the product has a JSON metafield at',
        'Tectonic.b2bChairsPageComparisonTableData.',
        'Current __b2bCompareData keys:',
        Object.keys(window.__b2bCompareData || {})
      );
    }
    return parseChairMetafield(raw);
  }

  /*
   * Compare data lives in the product metafield
   * Tectonic.b2bChairsPageComparisonTableData, pre-rendered server-side into
   * window.__b2bCompareData for products whose metafield is populated. Some
   * chairs in the collection do not yet carry that metafield locally, but the
   * same value is authoritative in Tectonic's product store. For those, fetch
   * the Typesense product doc on demand and read the metafield from it. The
   * endpoint reflects any Origin (CORS-open), so the browser fetch succeeds.
   * Results are cached back into __b2bCompareData so a chair is fetched once
   * per session. Returns the metafield object (parseChairMetafield accepts the
   * { field: { data } } shape) or null.
   */
  const TECTONIC_ORG = 'frido';
  async function fetchTectonicCompare(handle) {
    try {
      const res = await fetch(
        'https://api.tectonic.so/v1/org/' + TECTONIC_ORG + '/product/' + encodeURIComponent(handle) + '/typesense'
      );
      if (!res.ok) return null;
      const doc = await res.json();
      const mf = doc?.metafields?.['Tectonic.b2bChairsPageComparisonTableData'];
      // parseChairMetafield reads data.data || data.field.data, so either the
      // metafield object or its .field can be handed straight through.
      return mf?.field ? mf : (mf || null);
    } catch (_) {
      return null;
    }
  }

  async function getChairSpecsAsync(handle) {
    if (window.__b2bCompareData?.[handle]) return getChairSpecs(handle);
    const remote = await fetchTectonicCompare(handle);
    if (remote) {
      window.__b2bCompareData = window.__b2bCompareData || {};
      window.__b2bCompareData[handle] = remote; // cache for the session
      return parseChairMetafield(remote);
    }
    return getChairSpecs(handle); // logs the missing-data warning
  }

  /*
   * Param-row icons are authored in Tectonic as the "B2B Chairs Page Table
   * Index" entity definition — one entity per spec section, each carrying an
   * indexList of { Title, Icon.cdnUrl }. The per-product comparison metafield
   * holds only values, not icons, so the icon set is fetched separately and
   * keyed by the row Title (which matches the value rows' key verbatim, e.g.
   * "Recline (°)", "Armrest"). CORS-open, fetched once per session.
   */
  const TECTONIC_ICON_INDEX = 'b2b-chairs-page-table-index';
  // Row keys and icon Titles are authored independently, so they drift on case
  // ("Lumbar Support" vs "Lumbar support") and unit suffixes ("Product
  // Dimensions" vs "Product Dimensions (LxWxH)"). Match on a normalised form:
  // lowercased, parenthetical groups stripped, whitespace collapsed.
  function normParam(s) {
    return String(s || '').toLowerCase().replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }
  let _paramIconMap = null;
  async function fetchParamIconMap() {
    if (_paramIconMap) return _paramIconMap;
    _paramIconMap = {};
    try {
      const res = await fetch(
        'https://api.tectonic.so/proxy/atlantis/v1/org/' + TECTONIC_ORG +
        '/entity/bulkByDefinition?entityDefinitionSlug=' + TECTONIC_ICON_INDEX + '&page=1&pageSize=50'
      );
      if (!res.ok) return _paramIconMap;
      const json = await res.json();
      const hits = json?.hits || [];
      for (const hit of hits) {
        const list = hit?.defaultVariant?.data?.indexList || [];
        for (const row of list) {
          const title = normParam(row?.Title);
          const url = row?.Icon?.cdnUrl;
          if (title && url) _paramIconMap[title] = url;
        }
      }
    } catch (_) { /* leave map empty; rows fall back to the placeholder icon */ }
    return _paramIconMap;
  }

  /* Mirrors snippets/spectrum-color-swatch-hex.liquid so swatches stay
   * visually consistent across surfaces (PLP, PDP, compare table). */
  function colorNameToHex(value) {
    const v = String(value || '').toLowerCase();
    if (/black|onyx|jet|charcoal/.test(v)) return '#1a1a1a';
    if (/white|ivory|cream|pearl/.test(v)) return '#f3f1ec';
    if (/yellow|mustard|gold/.test(v))     return '#ffd400';
    if (/orange|rust|amber/.test(v))       return '#f57c00';
    if (/navy|royal/.test(v))              return '#1f2c5c';
    if (/aqua/.test(v))                    return '#3aa3a3';
    if (/sky|powder|baby blue/.test(v))    return '#9bc4e2';
    if (/blue|cobalt/.test(v))             return '#1976d2';
    if (/red|crimson|scarlet/.test(v))     return '#d32f2f';
    if (/pink|rose|blush/.test(v))         return '#ec407a';
    if (/maroon|wine|burgundy/.test(v))    return '#7a1f2b';
    if (/purple|violet|plum/.test(v))      return '#7b1fa2';
    if (/olive/.test(v))                   return '#6b6c2e';
    if (/sage/.test(v))                    return '#88a37b';
    if (/green/.test(v))                   return '#2e7d32';
    if (/teal|mint/.test(v))               return '#26a69a';
    if (/brown|tan|caramel|chocolate|mocha/.test(v)) return '#6d4c41';
    if (/beige|sand|nude/.test(v))         return '#d7c7a1';
    if (/silver|platinum/.test(v))         return '#c0c0c0';
    if (/grey|gray|graphite/.test(v))      return '#7a7a7a';
    return '#cfcfcf';
  }

  /* Extracts unique color swatches from a Shopify product JSON
   * (response shape from /products/{handle}.js). Returns an array of
   * { value, hex, imageUrl } — one entry per distinct color value, with
   * the variant's image as the swap target. Returns [] if the product
   * has no color/colour-style option. */
  function extractSwatches(product) {
    if (!product?.variants?.length) return [];

    // product.options may be ["Color", "Size"] (.js endpoint) OR
    // [{ name: "Color", values: [...] }, ...] (admin .json shape).
    const optionNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o?.name)).filter(Boolean);
    // Permissive match: anything containing "color"/"colour", case-insensitive.
    const colorIdx = optionNames.findIndex((name) => /colou?r/i.test(name));
    if (colorIdx < 0) {
      console.warn('[b2b-compare] No color option on', product.handle, '— options:', optionNames);
      return [];
    }

    const seen = new Set();
    const out = [];
    for (const v of product.variants) {
      // v.options is the array of selected option values for this variant.
      // Fall back to option1 / option2 / option3 if the array isn't present.
      const value = (v.options && v.options[colorIdx]) || v['option' + (colorIdx + 1)];
      if (!value || seen.has(value)) continue;
      seen.add(value);
      out.push({
        value,
        hex: colorNameToHex(value),
        // variant.featured_image is an object with .src, or null.
        // Fall back through product images so we always end up with a URL.
        imageUrl: v.featured_image?.src
          || product.featured_image
          || product.images?.[0]
          || '',
      });
    }
    return out;
  }

  const MOBILE_QUERY = '(max-width: 749px)';

  /* ----- <b2b-compare-dialog> custom element ----- */
  class B2BCompareDialog extends HTMLElement {
    connectedCallback() {
      this.runBtn        = this.querySelector('[data-compare-btn]');
      this.inlineMount   = this.querySelector('[data-compare-results-inline]');
      this.drawer        = this.querySelector('[data-compare-drawer]');
      this.drawerMount   = this.querySelector('[data-compare-results-drawer]');
      this.drawerHandle  = this.querySelector('[data-compare-drawer-handle]');

      this.runBtn?.addEventListener('click', () => this.run());

      // Custom pickers over the hidden <select>s. Each picker lists only chairs
      // not already chosen in another (visible) slot, so a chair can't be
      // compared against itself and the option count tracks live (5 across three
      // desktop slots, 6 across two mobile slots).
      this._slots = Array.from(this.querySelectorAll('[data-compare-slot]'));
      this._pickerSlots = Array.from(this.querySelectorAll('[data-slot-index]'));
      this.renderPickers();
      // Slot count changes across the mobile breakpoint (3 desktop / 2 mobile),
      // which changes what each picker may offer — re-render on crossing it.
      const mq = window.matchMedia(MOBILE_QUERY);
      (mq.addEventListener ? mq.addEventListener.bind(mq, 'change') : mq.addListener.bind(mq))(() => this.renderPickers());

      // Delegate close + chip-dropdown clicks (markup is rendered dynamically).
      this.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('[data-compare-close]');
        if (closeBtn) { this.drawer?.close(); return; }

        // Main page picker toggle → open/close its dropdown (close the others).
        const pToggle = e.target.closest('[data-picker-toggle]');
        if (pToggle) {
          const idx = pToggle.getAttribute('data-picker-toggle');
          const menu = this.querySelector(`[data-picker-menu="${idx}"]`);
          const willOpen = menu && menu.hidden;
          this.closePickerMenus();
          if (willOpen) { menu.hidden = false; pToggle.setAttribute('aria-expanded', 'true'); }
          return;
        }

        // Main page picker option → set the slot's chair and re-render pickers
        // (exclusions shift). Does not auto-run; the user hits Compare.
        const pOpt = e.target.closest('.b2b-compare__picker-option');
        if (pOpt) {
          const idx = pOpt.getAttribute('data-picker-index');
          const value = pOpt.getAttribute('data-value');
          const select = this.querySelector(`[data-slot-index="${idx}"] [data-compare-slot]`);
          this.closePickerMenus();
          if (select && select.value !== value) { select.value = value; this.renderPickers(); }
          return;
        }

        if (!e.target.closest('.b2b-compare__picker-menu')) this.closePickerMenus();

        // Chip toggle → open/close its own custom dropdown (close the others).
        const toggle = e.target.closest('[data-chip-toggle]');
        if (toggle) {
          const idx = toggle.getAttribute('data-chip-toggle');
          const menu = this.querySelector(`[data-chip-menu="${idx}"]`);
          const willOpen = menu && menu.hidden;
          this.closeChipMenus();
          if (willOpen) { menu.hidden = false; toggle.setAttribute('aria-expanded', 'true'); }
          return;
        }

        // Chip option → swap that chair and re-run in place (drawer stays open).
        const opt = e.target.closest('.b2b-compare__chip-option');
        if (opt) {
          const idx = parseInt(opt.getAttribute('data-chip-index'), 10);
          const value = opt.getAttribute('data-value');
          const slot = this._activeSlots && this._activeSlots[idx];
          this.closeChipMenus();
          if (slot && slot.value !== value) { slot.value = value; this.renderPickers(); this.run(); }
          return;
        }

        // Click anywhere else in the dialog closes any open dropdown.
        if (!e.target.closest('.b2b-compare__chip-menu')) this.closeChipMenus();
      });

      // Click backdrop to dismiss drawer.
      this.drawer?.addEventListener('click', (e) => {
        if (e.target === this.drawer) this.drawer.close();
      });

      // Any close path (button, backdrop, Esc) fires 'close' — release the lock.
      this.drawer?.addEventListener('close', () => this.unlockScroll());
    }

    closeChipMenus() {
      this.querySelectorAll('.b2b-compare__chip-menu').forEach((m) => { m.hidden = true; });
      this.querySelectorAll('[data-chip-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    }

    closePickerMenus() {
      this.querySelectorAll('.b2b-compare__picker-menu').forEach((m) => { m.hidden = true; });
      this.querySelectorAll('[data-picker-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    }

    // Slots the merchant can actually see at this breakpoint. The third slot is
    // display:none on mobile (offsetParent === null there), so it neither counts
    // toward exclusions nor gets compared.
    getVisibleSlots() {
      return (this._slots || []).filter((s) => {
        const wrap = s.closest('[data-slot-index]');
        return wrap && wrap.offsetParent !== null;
      });
    }

    selectedHandles() {
      return this.getVisibleSlots().map((s) => s.value).filter(Boolean);
    }

    // Repaint every picker: label = its own chair, menu = chairs not chosen in
    // any visible slot (so, like live, a picker offers only chairs you could
    // switch to — never a duplicate, never its own current pick).
    renderPickers() {
      const chosen = this.selectedHandles();
      (this._pickerSlots || []).forEach((wrap) => {
        const idx = wrap.getAttribute('data-slot-index');
        const select = wrap.querySelector('[data-compare-slot]');
        const label = wrap.querySelector('[data-picker-label]');
        const menu = wrap.querySelector('[data-picker-menu]');
        if (!select || !label || !menu) return;
        const currentOpt = Array.from(select.options).find((o) => o.value === select.value);
        label.textContent = currentOpt && currentOpt.value ? currentOpt.text : 'Choose a chair';
        const opts = Array.from(select.options).filter((o) => o.value && chosen.indexOf(o.value) === -1);
        menu.innerHTML = opts.map((o) =>
          `<button type="button" role="option" class="b2b-compare__picker-option" data-picker-index="${idx}" data-value="${this.escape(o.value)}">${this.escape(o.text)}</button>`
        ).join('');
      });
    }

    lockScroll() {
      if (this._scrollLocked) return;
      this._scrollLocked = true;
      this._scrollY = window.scrollY;
      // position:fixed pins the page even on iOS Safari, where overflow:hidden
      // alone still lets the background scroll behind a modal. Restored on close.
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${this._scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (!this._scrollLocked) return;
      this._scrollLocked = false;
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, this._scrollY || 0);
    }

    async run() {
      // Compare only the chairs in visible slots (two on mobile, three on
      // desktop). Keep the slot elements so the in-drawer chip dropdowns can map
      // back to a slot and change that chair without closing the popup.
      this._activeSlots = this.getVisibleSlots().filter((sel) => sel.value);
      const handles = this._activeSlots.map((sel) => sel.value);

      if (handles.length < 2) {
        this.showError('Pick at least 2 chairs to compare.');
        return;
      }

      this.setLoading(true);

      try {
        const [products, iconMap] = await Promise.all([
          Promise.all(
            handles.map((h) => fetch(`/products/${h}.js`).then((r) => {
              if (!r.ok) throw new Error(`Failed to fetch ${h}`);
              return r.json();
            }))
          ),
          fetchParamIconMap(),
        ]);

        const colorFillHandles = window.__b2bColorFillHandles || [];
        const chairs = await Promise.all(products.map(async (p) => {
          // Some products render their swatches as two-tone colour chips rather
          // than per-variant photos (configured per brand). The chip keeps its
          // imageUrl so clicking still swaps the main chair image; only the
          // swatch face is drawn as a colour fill instead of the photo.
          const colorFill = colorFillHandles.indexOf(p.handle) !== -1;
          const rawSwatches = extractSwatches(p);
          const defaultImage = rawSwatches[0]?.imageUrl || p.featured_image;
          const swatches = rawSwatches.map((sw) => ({ ...sw, colorFill }));
          const specData = await getChairSpecsAsync(p.handle);
          // Attach the section-index icon to each row by its title. Metafield
          // rows carry no icon of their own; a missing map entry falls back to
          // the placeholder icon in buildSpec.
          for (const section of (specData.specs || [])) {
            for (const row of section.rows) {
              const icon = iconMap[normParam(row.key)];
              if (!row.icon && icon) row.icon = icon;
            }
          }
          return {
            handle: p.handle,
            title: p.title,
            image: defaultImage,
            url: p.url,
            swatches,
            ...specData,
          };
        }));

        const isMobile = window.matchMedia(MOBILE_QUERY).matches;

        if (isMobile) {
          // Sticky chip header goes into its own slot so it pins reliably
          // inside the scrolling drawer; body fills the remaining area.
          this.drawerHandle.innerHTML = this.buildDrawerHeader(chairs);
          this.drawerMount.innerHTML  = this.buildHtml(chairs, { mobile: true });
          if (!this.drawer?.open) {
            if (typeof this.drawer?.showModal === 'function') {
              this.drawer.showModal();
            } else {
              this.drawer?.setAttribute('open', '');
            }
          }
          // Lock the page behind the drawer — a modal <dialog> alone doesn't
          // reliably stop touch-scroll of the background on mobile.
          this.lockScroll();
        } else {
          this.inlineMount.innerHTML = this.buildHtml(chairs, { mobile: false });
          this.inlineMount.hidden = false;
          this.inlineMount.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (err) {
        console.error('[b2b-compare] fetch failed', err);
        this.showError('Sorry — we could not load comparison data. Try again.');
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.runBtn) return;
      if (loading) {
        this.runBtn.disabled = true;
        this.runBtn.dataset.originalLabel = this.runBtn.textContent;
        this.runBtn.textContent = 'Comparing…';
      } else {
        this.runBtn.disabled = false;
        this.runBtn.textContent = this.runBtn.dataset.originalLabel || 'Compare';
      }
    }

    buildHtml(chairs, { mobile = false } = {}) {
      const count = chairs.length;

      // No results title here. Desktop already shows the selector panel's
      // heading right above; a second "Which Chair To Choose?" over the results
      // is a duplicate the reference doesn't have. Mobile's title lives in the
      // drawer's sticky header (buildDrawerHeader), not here.
      const headerHtml = '';

      const chairsHtml = `
        <div class="b2b-compare__chairs" style="--chair-count:${count};">
          ${chairs.map((c) => this.buildChairCard(c)).join('')}
        </div>
      `;

      // Union of sections + rows across all chairs (first occurrence wins
      // for ordering). Lets the merchant control which sections appear by
      // editing the metafield — no JS change needed.
      const schema = this.unifiedSchema(chairs);
      const sectionsHtml = schema.map((section) => this.buildSpec(section, chairs)).join('');

      // --chair-count-extra = (count - 1) is used by mobile to lay out
      // the remaining chairs in the row beside the label (first chair's
      // value sits next to the label).
      return `
        <div class="b2b-compare__results"
             style="--chair-count:${count}; --chair-count-extra:${Math.max(1, count - 1)};">
          ${headerHtml}
          ${chairsHtml}
          ${sectionsHtml}
        </div>
      `;
    }

    buildDrawerHeader(chairs) {
      // Chair options come from the compare slots (same product list). Each chip
      // is a custom dropdown (not a native <select>, whose option text can't be
      // styled to match live). Options already shown in another chip are omitted
      // so the same chair can't be picked twice (item 6).
      const slot = this.querySelector('[data-compare-slot]');
      const allOpts = slot ? Array.from(slot.options).filter((o) => o.value) : [];
      const chosen = chairs.map((c) => c.handle);
      return `
        <h3 class="b2b-compare__results-title">Which Chair To Choose ?</h3>
        <div class="b2b-compare__chip-row">
          ${chairs.map((c, i) => {
            const opts = allOpts.filter((o) => o.value === c.handle || chosen.indexOf(o.value) === -1);
            return `
              <div class="b2b-compare__chip-wrap">
                <button type="button" class="b2b-compare__chip" data-chip-toggle="${i}" aria-haspopup="listbox" aria-expanded="false">
                  <span class="b2b-compare__chip-name">${this.escape(c.title)}</span>
                  <svg class="b2b-compare__chip-caret" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                    <path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="M5 8l5 5 5-5"/>
                  </svg>
                </button>
                <div class="b2b-compare__chip-menu" role="listbox" data-chip-menu="${i}" data-align="${i === chairs.length - 1 ? 'right' : 'left'}" hidden>
                  ${opts.map((o) => `
                    <button type="button" role="option" class="b2b-compare__chip-option${o.value === c.handle ? ' is-selected' : ''}"
                      data-chip-index="${i}" data-value="${this.escape(o.value)}" aria-selected="${o.value === c.handle ? 'true' : 'false'}">
                      <span>${this.escape(o.textContent)}</span>
                      ${o.value === c.handle
                        ? `<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M4 10l4 4 8-8"/></svg>`
                        : ''}
                    </button>
                  `).join('')}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `;
    }

    // The two-tone chip pairs the variant's colour with the chair's dark body.
    // A dark neutral grey reads almost black next to that body half, so lighten
    // dark greys to a light neutral (like live); vivid colours pass through.
    twoToneAccent(hex) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return hex;
      const n = parseInt(m[1], 16);
      const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      const isNeutral = Math.max(r, g, b) - Math.min(r, g, b) <= 12;
      return isNeutral && Math.max(r, g, b) < 170 ? '#d9d9d9' : hex;
    }

    buildChairCard(c) {
      const swatches = c.swatches || [];
      const swatchesHtml = swatches.map((sw, i) => `
        <button type="button"
          class="b2b-compare__chair-color${i === 0 ? ' b2b-compare__chair-color--active' : ''}"
          data-b2b-swatch
          data-image-url="${this.escape(sw.imageUrl || '')}"
          data-color-value="${this.escape(sw.value)}"
          title="${this.escape(sw.value)}"
          aria-label="${this.escape(sw.value)}"
          aria-pressed="${i === 0 ? 'true' : 'false'}"
        >
          ${sw.colorFill
            // Two-tone chip: the variant's accent colour on the left, the chair's
            // dark body on the right — a vertical half-and-half split like live.
            ? `<span class="b2b-compare__chair-color-fill" style="background:linear-gradient(90deg, ${this.twoToneAccent(sw.hex)} 0 50%, #1a1a1a 50% 100%);"></span>`
            : (sw.imageUrl
              ? `<img src="${this.thumb(sw.imageUrl, 120)}" alt="" loading="lazy" width="40" height="40">`
              : `<span class="b2b-compare__chair-color-fill" style="background:${sw.hex};"></span>`)}
        </button>
      `).join('');

      return `
        <div class="b2b-compare__chair">
          <div class="b2b-compare__chair-name-card">
            <a class="b2b-compare__chair-name" href="${c.url || '#'}">${this.escape(c.title)}</a>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="M9 6l6 6-6 6"/>
            </svg>
          </div>
          <div class="b2b-compare__chair-image">
            ${c.image ? `<img data-b2b-card-img src="${c.image}" alt="${this.escape(c.title)}" loading="lazy" width="240" height="240">` : ''}
          </div>
          ${swatchesHtml ? `<div class="b2b-compare__chair-colors">${swatchesHtml}</div>` : ''}
          ${c.category ? `<p class="b2b-compare__chair-category">${this.escape(c.category)}</p>` : ''}
          ${c.description ? `<p class="b2b-compare__chair-desc">${this.escape(c.description)}</p>` : ''}
        </div>
      `;
    }

    /* Build the union of sections + rows across the selected chairs.
     * Preserves first-seen order so the merchant controls section ordering
     * by editing the metafield. Icon is taken from the first chair that
     * supplies a non-empty `icon` for that row key. */
    unifiedSchema(chairs) {
      const out = [];
      const bySection = new Map();
      for (const chair of chairs) {
        for (const section of (chair.specs || [])) {
          let entry = bySection.get(section.title);
          if (!entry) {
            entry = { title: section.title, rows: [], _seen: new Map() };
            bySection.set(section.title, entry);
            out.push(entry);
          }
          for (const row of section.rows) {
            const existing = entry._seen.get(row.key);
            if (!existing) {
              const rowObj = { key: row.key, icon: row.icon || '' };
              entry._seen.set(row.key, rowObj);
              entry.rows.push(rowObj);
            } else if (!existing.icon && row.icon) {
              existing.icon = row.icon;
            }
          }
        }
      }
      // Strip the bookkeeping field before returning.
      return out.map(({ title, rows }) => ({ title, rows }));
    }

    /* Look up a chair's value for `(sectionTitle, rowKey)`. Returns '—' if
     * the chair doesn't define that row. */
    lookupValue(chair, sectionTitle, rowKey) {
      const sec = (chair.specs || []).find((s) => s.title === sectionTitle);
      if (!sec) return '—';
      const row = sec.rows.find((r) => r.key === rowKey);
      return row?.value || '—';
    }

    buildSpec(section, chairs) {
      // Each value cell embeds its own label so mobile can render the row
      // as a 3-column grid of stacked label-above-value cells (no sticky
      // left column). Desktop hides those per-cell labels and uses the
      // single shared `.spec-label` on the left of the row instead.
      const rowsHtml = section.rows.map((row) => {
        const values = chairs.map((c) => this.lookupValue(c, section.title, row.key));
        const valuesHtml = values.map((v, i) => `
          <div class="b2b-compare__spec-value" data-chair-index="${i}">
            <span class="b2b-compare__spec-cell-label">${this.escape(row.key)}</span>
            <span class="b2b-compare__spec-cell-value">${this.escape(v)}</span>
          </div>
        `).join('');

        const iconHtml = row.icon
          ? `<img class="b2b-compare__spec-icon b2b-compare__spec-icon--img" src="${this.thumb(row.icon, 64)}" alt="" loading="lazy" width="28" height="28">`
          : `<svg class="b2b-compare__spec-icon" viewBox="0 0 24 24" aria-hidden="true">
               <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
             </svg>`;

        return `
          <div class="b2b-compare__spec-row">
            <div class="b2b-compare__spec-label">
              ${iconHtml}
              <span class="b2b-compare__spec-label-text">${this.escape(row.key)}</span>
              <span class="b2b-compare__spec-label-value">${this.escape(values[0])}</span>
            </div>
            <div class="b2b-compare__spec-values">
              ${valuesHtml}
            </div>
          </div>
        `;
      }).join('');

      return `
        <details class="b2b-compare__spec">
          <summary class="b2b-compare__spec-header">
            <span class="b2b-compare__spec-title">${this.escape(section.title)}</span>
            <svg class="b2b-compare__spec-caret" viewBox="0 0 20 20" aria-hidden="true">
              <path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="M5 7l5 5 5-5"/>
            </svg>
          </summary>
          <div class="b2b-compare__spec-rows">${rowsHtml}</div>
        </details>
      `;
    }

    escape(s) {
      return String(s ?? '').replace(/[&<>"']/g, (ch) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[ch]));
    }

    /* Rewrites a Shopify CDN URL to request a specific width. Cheap thumb
     * generation so swatches don't ship 1024px chair photos as 36px circles. */
    thumb(url, width) {
      if (!url) return '';
      if (/[?&]width=/.test(url)) return url.replace(/width=\d+/, 'width=' + width);
      return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
    }

    showError(msg) {
      const region = this.querySelector('[data-compare-status]');
      if (!region) { alert(msg); return; }
      region.textContent = msg;
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(() => { region.textContent = ''; }, 4000);
    }
  }

  if (!customElements.get('b2b-compare-dialog')) {
    customElements.define('b2b-compare-dialog', B2BCompareDialog);
  }

  /* ----- Smooth scroll for in-page CTAs that target the hero form ----- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-b2b-quote-target], a[href="#b2b-lead-form"]');
    if (!link) return;
    const target = document.getElementById('b2b-lead-form');
    if (!target) return;
    e.preventDefault();
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
    const firstField = target.querySelector('input, select, textarea, button');
    if (firstField) {
      setTimeout(() => firstField.focus({ preventScroll: true }), prefersReduced ? 0 : 400);
    }
  });

  /* ----- B2B product card + compare table: color swatch → image swap -----
   * Same data attributes (`data-b2b-swatch` / `data-b2b-card-img`) are used
   * on both surfaces. The closest known container marks the swap scope. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-b2b-swatch]');
    if (!btn) return;
    e.preventDefault();
    const card = btn.closest('.b2b-card, .b2b-compare__chair');
    if (!card) return;
    const imgEl = card.querySelector('[data-b2b-card-img]');
    const newUrl = btn.getAttribute('data-image-url');
    if (imgEl && newUrl && imgEl.tagName === 'IMG') {
      const base = newUrl.split('?')[0];
      const query = newUrl.includes('?') ? newUrl.slice(newUrl.indexOf('?')) : '';
      const widths = [270, 360, 540, 720];
      const srcset = widths.map((w) => {
        const sep = query.includes('width=')
          ? query.replace(/width=\d+/, 'width=' + w)
          : (query ? query + '&width=' + w : '?width=' + w);
        return base + sep + ' ' + w + 'w';
      }).join(', ');
      imgEl.src = newUrl;
      imgEl.srcset = srcset;
      imgEl.alt = btn.getAttribute('data-color-value') || imgEl.alt;
    }
    // Update active state across the surface's swatch group.
    const list = btn.closest('.b2b-card__swatches, .b2b-compare__chair-colors');
    if (list) {
      list.querySelectorAll('[data-b2b-swatch]').forEach((other) => {
        const active = other === btn;
        other.classList.toggle('b2b-card__swatch--active', active);
        other.classList.toggle('b2b-compare__chair-color--active', active);
        other.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
  });
})();
